/**
 * Per-apartment money-flow math for the admin Revenue surfaces.
 *
 * Pure and dependency-light (formatting only) so the expected-vs-live figures
 * the Revenue tab and the apartment sidebar print can be unit-tested directly
 * — these are the numbers we invoice owners against, so a silent drift here is
 * a wrong figure on a management invoice, not a cosmetic bug.
 */
import { formatEUR } from "@/lib/v2-format";
import { fullRentMasterRentUnset } from "@/lib/owner-metrics";
import type {
  Apartment,
  CollectionMode,
  EffectiveRevenue,
  RevenueModel,
} from "@/types/property";

// ─── Expected rent for a month ───────────────────────────────────────────────

/** Contracted rent for one month, resolved room by room. */
export type ApartmentExpectedRent = {
  /** Contracted rent for the month — the Expected headline. */
  total: number;
  /**
   * Rooms under contract for the month, and so the per-room fee basis. Counts
   * a room whose rent is 0 or missing: the fee follows the LET UNIT, exactly
   * as `generate_management_invoices` bills occupied units.
   */
  rooms: number;
  /** Per-room expected rent (0 for a room under no contract), for the breakdown. */
  byRoom: Map<number, number>;
};

/**
 * What this apartment SHOULD collect for one month, per active contract.
 *
 * A room contributes, in precedence order:
 *   1. the month's own rent charges, when it has any — that is the contract
 *      that was actually billed, and it keeps a HISTORICAL month right: a
 *      tenant who has since moved out still counted that month, where reading
 *      today's occupancy would drop the room and print an expected figure
 *      BELOW the live one;
 *   2. else its list price, if a tenant lives there now — the forecast for a
 *      let room the monthly rent run has not reached yet;
 *   3. else nothing. A vacant room has no contract, so it must not inflate the
 *      forecast or attract a per-room fee we will never invoice.
 *
 * Because paid charges are a subset of billed ones, `total` is always ≥ the
 * live figure — the Expected/Live pair can never read back to front.
 */
export function resolveExpectedMonthRent(
  rooms: readonly { id: number; price: number | string | null }[],
  /** Rooms with an active occupant right now. */
  occupiedRoomIds: ReadonlySet<number>,
  /** The month's rent charges (`useApartmentMonthRent().rows`), paid or not. */
  monthRentRows: readonly { roomId: number | null; amount: number }[]
): ApartmentExpectedRent {
  const billedByRoom = new Map<number, number>();
  for (const row of monthRentRows) {
    if (row.roomId == null) continue;
    billedByRoom.set(
      row.roomId,
      (billedByRoom.get(row.roomId) ?? 0) + (Number(row.amount) || 0)
    );
  }

  const byRoom = new Map<number, number>();
  let total = 0;
  let contractedRooms = 0;
  for (const room of rooms) {
    const billed = billedByRoom.get(room.id);
    const occupied = occupiedRoomIds.has(room.id);
    const expected =
      billed != null ? billed : occupied ? Number(room.price) || 0 : 0;
    byRoom.set(room.id, expected);
    if (billed != null || occupied) contractedRooms += 1;
    total += expected;
  }
  return { total, rooms: contractedRooms, byRoom };
}

// ─── Money flow ──────────────────────────────────────────────────────────────

export type ApartmentEconomics = {
  model: RevenueModel;
  collectionMode: CollectionMode;
  companyCollects: boolean;
  /** Gross tenant rent for the month. */
  rentCollected: number;
  /** Who receives tenant rent first, given the collection mode. */
  rentCollector: "coliviq" | "Owner";
  /** CoLiviq's total monthly management revenue (margin / %-fee / flat-fee). */
  coliviqRevenue: number;
  /** Human-readable formula behind coliviqRevenue. */
  coliviqFormula: string;
  /**
   * The same formula with the rent left as the word "rent" instead of a figure.
   * The expected/live cards show one formula above two different rent numbers,
   * so a formula quoting either of them would contradict the other.
   */
  coliviqFormulaGeneric: string;
  /** The headline fee for %/flat models (null for arbitrage). */
  feeLabel: string | null;
  /** What the owner ends up with each month. */
  ownerReceives: number;
  ownerReceivesLabel: string;
  /** Sidebar-width variant of ownerReceivesLabel. */
  ownerReceivesShortLabel: string;
  masterRent: number;
};

/**
 * Resolve the per-apartment money math for the active revenue model. Shared by
 * the Revenue tab and the sidebar so the two never disagree.
 *   - full_rent (arbitrage): CoLiviq keeps tenant rent − master rent; owner
 *     gets the fixed master rent.
 *   - pct_of_rent: CoLiviq fee = rent × pct; owner gets rent − fee.
 *   - flat_fee_per_room: CoLiviq fee = fee/room × rooms; owner gets rent − fee.
 *
 * Run twice — once on contracted rent, once on the rent that actually arrived
 * — it produces the expected and live readings side by side. Every input has
 * to be swapped for the live run, not just the rent: see `feeRooms`.
 */
export function computeApartmentEconomics(
  apartment: Apartment,
  roomsRevenue: number,
  revenue: EffectiveRevenue,
  /**
   * Rooms the per-room fee applies to. Required, and deliberately not derived
   * from `apartments.num_rooms`, because the right count differs per reading
   * and neither is the room total:
   *   - EXPECTED → rooms under contract for the month
   *     (`resolveExpectedMonthRent().rooms`). `generate_management_invoices`
   *     bills occupied units, so billing a vacant room here would forecast a
   *     fee we never invoice.
   *   - LIVE → rooms whose rent actually came in (`liveRent.paidRooms`). A
   *     per-room fee is only earned on rent CoLiviq has collected; on the full
   *     count a month with nothing paid read as a full €99 fee and a NEGATIVE
   *     net to the owner, which is neither what we invoice nor what we owe.
   */
  feeRooms: number
): ApartmentEconomics {
  const companyCollects = revenue.collection_mode === "company_collects";
  const rentCollected = roomsRevenue;
  const master = apartment.master_rent ?? 0;
  const numRooms = feeRooms;
  const eur = (n: number) => formatEUR(n) ?? "€0";

  // full_rent's fee is the sublease margin, rent − master rent. With no master
  // rent on file there is no margin to state: `rent − 0` is the ENTIRE rent,
  // and `generate_management_invoices` (20260908140000) plus the owner-profile
  // forecast (unitMonthlyRevenue) both refuse to bill that. Asserting it as
  // earned revenue here would leave this panel claiming a fee the invoice
  // generator will never issue — the same figure, read two ways, on the same
  // apartment. Same helper, so the two cannot drift apart again.
  const masterRentUnset = fullRentMasterRentUnset({
    revenue_model: revenue.revenue_model,
    coliviq_fee_pct: revenue.coliviq_fee_pct,
    coliviq_fee_amount: revenue.coliviq_fee_amount,
    master_rent: apartment.master_rent,
  });

  let coliviqRevenue = masterRentUnset ? 0 : rentCollected - master;
  let coliviqFormula = masterRentUnset
    ? "Master rent not set — no fee is billed"
    : `${eur(rentCollected)} rent − ${eur(master)} master rent`;
  let coliviqFormulaGeneric = masterRentUnset
    ? "Master rent not set — no fee is billed"
    : `rent − ${eur(master)} master rent`;
  let feeLabel: string | null = null;
  let ownerReceives = master;
  let ownerReceivesLabel = "Master rent to owner";
  let ownerReceivesShortLabel = "Master rent";

  if (revenue.revenue_model === "pct_of_rent") {
    const pct = revenue.coliviq_fee_pct ?? 0;
    coliviqRevenue = (rentCollected * pct) / 100;
    feeLabel = `${pct}%`;
    coliviqFormula = `${pct}% × ${eur(rentCollected)} rent`;
    coliviqFormulaGeneric = `${pct}% × rent`;
    ownerReceives = rentCollected - coliviqRevenue;
    ownerReceivesLabel = companyCollects
      ? "Net rent to owner"
      : "Owner keeps (after fee)";
    ownerReceivesShortLabel = companyCollects ? "Net to owner" : "Owner keeps";
  } else if (revenue.revenue_model === "flat_fee_per_room") {
    const perRoom = revenue.coliviq_fee_amount ?? 0;
    coliviqRevenue = perRoom * numRooms;
    feeLabel = `${eur(perRoom)} / room`;
    coliviqFormula = `${eur(perRoom)}/room × ${numRooms} ${
      numRooms === 1 ? "room" : "rooms"
    }`;
    // The room count now differs between the expected and live runs, so the
    // shared hint leaves it out the same way pct_of_rent leaves out the rent.
    coliviqFormulaGeneric = `${eur(perRoom)}/room × rooms`;
    ownerReceives = rentCollected - coliviqRevenue;
    ownerReceivesLabel = companyCollects
      ? "Net rent to owner"
      : "Owner keeps (after fee)";
    ownerReceivesShortLabel = companyCollects ? "Net to owner" : "Owner keeps";
  }

  return {
    model: revenue.revenue_model,
    collectionMode: revenue.collection_mode,
    companyCollects,
    rentCollected,
    rentCollector: companyCollects ? "coliviq" : "Owner",
    coliviqRevenue,
    coliviqFormula,
    coliviqFormulaGeneric,
    feeLabel,
    ownerReceives,
    ownerReceivesLabel,
    ownerReceivesShortLabel,
    masterRent: master,
  };
}
